import { BaseService } from "../services/baseService";
import { logger, LOG_CATEGORIES } from "../utils/logger";
import CryptoJS from "crypto-js";

// In a real app, this key should be managed securely via environment variables
// and should not be hardcoded.
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "demo-very-secret-key";

class MessagesService extends BaseService {
    constructor() {
        super("messages", "messages", "Message");
    }

    /**
     * Sends an encrypted message to Firestore.
     * @param {string} userId - The ID of the user sending the message.
     * @param {string} userEmail - The email of the user.
     * @param {string} messageText - The raw text of the message.
     */
    async sendMessage(userId, userEmail, messageText) {
        if (!messageText) {
            throw new Error("Message text cannot be empty.");
        }
        
        try {
            const encryptedText = CryptoJS.AES.encrypt(messageText, ENCRYPTION_KEY).toString();
            
            const messageData = {
                text: encryptedText,
                userId: userId,
                userEmail: userEmail,
                encrypted: true,
                messageType: "user_message"
            };

            const messageId = this.generateId();
            const result = await this.create(messageId, messageData);

            await logger.info(LOG_CATEGORIES.MESSAGES, "Message sent successfully", { 
                userId, 
                messageId: result.id 
            });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.MESSAGES, "Failed to send message", error, { userId });
            throw new Error("Failed to send message.");
        }
    }

    /**
     * Fetches all messages for a specific user.
     * @param {string} userId - The ID of the user whose messages to fetch.
     * @returns {Promise<Array>} A promise that resolves to an array of message objects.
     */
    async getUserMessages(userId) {
        if (!userId) {
            return [];
        }
        
        try {
            const messages = await this.getAll(
                { userId },
                { orderBy: { field: "createdAt", direction: "desc" } }
            );

            // Filter out messages that can't be decrypted properly
            const decryptedMessages = messages.map(message => {
                try {
                    const decryptedText = this.decryptMessage(message.text);
                    return {
                        ...message,
                        decryptedText: decryptedText,
                    };
                } catch (error) {
                    // Skip corrupted messages
                    return null;
                }
            }).filter(msg => msg !== null); // Remove null entries

            await logger.debug(LOG_CATEGORIES.MESSAGES, "Messages retrieved for user", { 
                userId, 
                count: decryptedMessages.length 
            });

            return decryptedMessages;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.MESSAGES, "Failed to get user messages", error, { userId });
            return []; // Return empty array on error to prevent UI crashes
        }
    }

    /**
     * Decrypts a message text.
     * @param {string} encryptedText - The encrypted message.
     * @returns {string} The decrypted text, or an error message if decryption fails.
     */
    decryptMessage(encryptedText) {
        try {
            // Check if the encrypted text is valid
            if (!encryptedText || typeof encryptedText !== "string") {
                return "[Invalid message data]";
            }

            const bytes = CryptoJS.AES.decrypt(encryptedText, ENCRYPTION_KEY);
        
            // Try to convert to UTF-8 string with error handling
            let decryptedText;
            try {
                decryptedText = bytes.toString(CryptoJS.enc.Utf8);
            } catch (utf8Error) {
                // If UTF-8 conversion fails, try alternative encoding
                try {
                    decryptedText = bytes.toString(CryptoJS.enc.Latin1);
                } catch (latin1Error) {
                    return "[Message encoding error]";
                }
            }

            if (!decryptedText) {
                return "[Empty message]";
            }
        
            return decryptedText;
        } catch (error) {
            return "[Message unavailable]";
        }
    }

    /**
     * Create a system message
     * @param {string} userId - The ID of the user receiving the message.
     * @param {string} messageText - The message text.
     * @param {string} messageType - The type of system message.
     */
    async createSystemMessage(userId, messageText, messageType = "system") {
        try {
            const messageData = {
                text: messageText,
                userId: userId,
                userEmail: null,
                encrypted: false,
                messageType: messageType,
                isSystemMessage: true
            };

            const messageId = this.generateId();
            const result = await this.create(messageId, messageData);

            await logger.info(LOG_CATEGORIES.MESSAGES, "System message created", { 
                userId, 
                messageId: result.id,
                messageType 
            });

            return result;
        } catch (error) {
            await logger.error(LOG_CATEGORIES.MESSAGES, "Failed to create system message", error, { userId });
            throw new Error("Failed to create system message");
        }
    }
}

export const messagesService = new MessagesService(); 